import { FileReaderHelper } from '@/helpers/fileReaderHelper';
import { FundClassification } from '@/models/common/FundClassification';
import { FundGoals } from '@/models/common/FundGoals';
import type { DocumentDtoOut } from '@/models/out/DocumentDtoOut';
import type { OpenFundFormData } from '../schemas/openFundFormSchema';

const stripDataUrl = (dataUrl: string): string => {
  const commaIndex = dataUrl.indexOf(',');
  return commaIndex >= 0 ? dataUrl.substring(commaIndex + 1) : dataUrl;
};

const fileToDoc = async (file: File, description?: string): Promise<DocumentDtoOut> => {
  const docs = await FileReaderHelper.fileToDocumentDtoOut(file, description);
  return docs[0];
};

// Convert a form file field to a document only when a new File was chosen; otherwise undefined
// (the server keeps the previously-uploaded document).
const optionalDoc = async (
  file: File | null | undefined,
  description: string
): Promise<DocumentDtoOut | undefined> => (file instanceof File ? fileToDoc(file, description) : undefined);

const mapNewAdditionalDocs = async (
  docs: { name?: string; file?: File | null }[] | undefined
): Promise<DocumentDtoOut[]> => {
  const withFiles = (docs ?? []).filter((doc) => doc.file instanceof File);
  return Promise.all(withFiles.map((doc) => fileToDoc(doc.file as File, doc.name || 'additional-document')));
};

/**
 * Builds the body for the anonymous "resubmit" (completion) endpoint.
 * Text/select/flag fields are always included. Documents are included only when the applicant
 * chose a new file (replacement); untouched documents are omitted so the server keeps them.
 * Newly added additional documents are sent to be appended. The signature is always re-sent.
 */
export const mapFormDataToResubmitDto = async (data: OpenFundFormData) => {
  const isWelfare =
    data.fundClassification === FundClassification.Welfare ||
    (data as { fundClassification: FundClassification }).fundClassification ===
      FundClassification.CampaignForWelfare;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const d = data as any;

  const mappedFormCoordinators = await Promise.all(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (d.coordinators ?? []).map(async (c: any) => ({
      firstName: c.firstName,
      lastName: c.lastName,
      israeliId: c.israeliId,
      city: c.city,
      street: c.street,
      houseNumber: c.houseNumber,
      email: c.email,
      phone: c.phone,
      relationToSupported: c.relationToSupported || undefined,
      idCardPhotocopy: await optionalDoc(c.idCardPhotocopy, 'coordinator-id-card'),
      idAppendixPhotocopy: await optionalDoc(c.idAppendixPhotocopy, 'coordinator-id-appendix'),
      newAdditionalDocuments: await mapNewAdditionalDocs(c.additionalDocuments),
      spouseFirstName: c.spouseFirstName || undefined,
      spouseIsraeliId: c.spouseIsraeliId || undefined,
    }))
  );

  // When "the supported is the coordinator" is on, the server's stored coordinators start with one
  // entry per supported. Re-create those (text only; documents are kept server-side by index) and
  // prepend them so the list stays aligned with the server, exactly as the original submission did.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const supportedAsCoordinators = isWelfare && d.isCoordinator
    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (d.supporteds ?? []).map((s: any) => ({
        firstName: s.firstName,
        lastName: s.lastName,
        israeliId: s.israeliId,
        city: s.city,
        street: s.street,
        houseNumber: s.houseNumber,
        email: s.email,
        phone: s.phone,
        relationToSupported: undefined,
        idCardPhotocopy: undefined,
        idAppendixPhotocopy: undefined,
        newAdditionalDocuments: [],
        spouseFirstName: undefined,
        spouseIsraeliId: undefined,
      }))
    : [];

  const coordinators = [...supportedAsCoordinators, ...mappedFormCoordinators];

  const supporteds = isWelfare
    ? await Promise.all(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (d.supporteds ?? []).map(async (s: any) => ({
          firstName: s.firstName,
          lastName: s.lastName,
          israeliId: s.israeliId,
          city: s.city,
          street: s.street,
          houseNumber: s.houseNumber,
          email: s.email,
          phone: s.phone,
          idCardPhotocopy: await optionalDoc(s.idCardPhotocopy, 'supported-id-card'),
          idAppendixPhotocopy: await optionalDoc(s.idAppendixPhotocopy, 'supported-id-appendix'),
          newAdditionalDocuments: await mapNewAdditionalDocs(s.additionalDocuments),
          relationshipToFirstSupported: s.relationshipToFirstSupported,
        }))
      )
    : [];

  return {
    fundName: data.fundName,
    fundClassification: data.fundClassification,
    fundGoals: (d.fundGoals ?? []) as FundGoals[],
    otherGoalDescription: d.otherGoalDescription || undefined,
    reasonSummary: data.reasonSummary || undefined,
    responsibleParty: isWelfare ? undefined : d.responsibleParty,
    responsibleEntityName: isWelfare ? undefined : d.responsibleEntityName,
    supporteds,
    coordinators,
    applicantDetails: {
      firstName: coordinators[0]?.firstName ?? '',
      lastName: coordinators[0]?.lastName ?? '',
      email: coordinators[0]?.email ?? '',
    },
    signature: {
      name: 'signature.png',
      description: 'signature',
      contentBase64: stripDataUrl(data.signature),
    },
    personalInterfaceInNedarimPlus: data.personalInterfaceInNedarimPlus,
    crowdfundingCampaign: data.crowdfundingCampaign,
    regulationsAccepted: d.regulationsAccepted ?? false,
    supportedAwareOfFund: isWelfare ? d.supportedAwareOfFund : undefined,
    supportedCanViewFund: isWelfare ? d.supportedCanViewFund : undefined,
  };
};
